// SF1 — Orders by Category.
//
// Two ways of counting sit on one row and they are not the same. Revenue and
// units come from every order line, so they sum to the catalogue total. The
// order column comes from a contest run once per order, which counts that order
// in the single category contributing most of its value — so it sums to the
// number of orders, not to anything additive across lines. The column is
// labelled primary-category orders for exactly that reason.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderAnalytics.module.css';
import { getCategories } from '../services/orderAnalyticsApi';

const COLUMNS = [
  { key: 'rank', label: '#' },
  { key: 'category', label: 'Category' },
  { key: 'revenue', label: 'Revenue', numeric: true },
  { key: 'units', label: 'Units', numeric: true },
  { key: 'orders', label: 'Primary-category orders', numeric: true },
  { key: 'share', label: 'Share', numeric: true },
  { key: 'revenue_change', label: 'Revenue trend', numeric: true },
  { key: 'units_change', label: 'Units trend', numeric: true },
];

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const money = (value) =>
  value === null || value === undefined ? <Blank /> : `₹${inr.format(Math.round(value))}`;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

// Absent, not flat. A category with no prior period has no trend.
const Change = ({ value }) => {
  if (value === null || value === undefined) return <Blank />;
  const up = value > 0;
  return (
    <span className={up ? local.changeUp : local.changeDown}>
      {up ? '+' : ''}{(value * 100).toFixed(1)}%
    </span>
  );
};

const CategoryBreakdown = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [grain, setGrain] = useState('browse_classification');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getCategories(token, { start, end, grain }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, grain, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const rows = data?.rows || [];
  const grains = data?.grains || [
    { key: 'browse_classification', label: 'Category' },
    { key: 'display_group', label: 'Department' },
  ];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Orders by category</h2>
        <div className={local.headAside}>
          {data && <span className={local.muted}>{count(data.orders)} orders</span>}
          <div className={local.segmented}>
            {grains.map((option) => (
              <button
                key={option.key}
                type="button"
                className={option.key === grain ? local.segmentOn : local.segment}
                onClick={() => setGrain(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className={local.muted}>
        Revenue and units come from every order line and sum to the catalogue
        total. Orders are counted once each, in the category that contributed
        most of that order's value, so the two columns are not comparable.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the breakdown'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column.key} className={column.numeric ? local.numeric : undefined}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className={local.emptyCell} colSpan={COLUMNS.length}>
                    {loading ? 'Loading…' : 'No orders in this period.'}
                  </td>
                </tr>
              ) : rows.map((row, index) => (
                <tr key={row.category}>
                  <td className={local.rankCell}>{index + 1}</td>
                  <td className={row.uncategorised ? local.uncategorised : undefined}>
                    {row.category}
                  </td>
                  <td className={local.numeric}>{money(row.revenue)}</td>
                  <td className={local.numeric}>{count(row.units)}</td>
                  <td className={local.numeric}>{count(row.primary_category_orders)}</td>
                  <td className={`${local.numeric} ${local.shareCell}`}>
                    <span
                      className={local.shareBar}
                      style={{ width: `${(row.share_of_revenue || 0) * 100}%` }}
                    />
                    <span className={local.shareValue}>
                      {row.share_of_revenue === null || row.share_of_revenue === undefined
                        ? <Blank /> : `${(row.share_of_revenue * 100).toFixed(1)}%`}
                    </span>
                  </td>
                  <td className={local.numeric}><Change value={row.revenue_change} /></td>
                  <td className={local.numeric}><Change value={row.units_change} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default CategoryBreakdown;
